import { StyleSheet, View } from 'react-native';

import { Pill, type PillTone } from '@/components/Pill';
import { Text } from '@/components/Text';
import { spacing } from '@/constants/theme';
import type { StatusCard } from '@/types/deal';

export type SocialHeroStatusProps = {
  /** A finished deal's card from the server: "Approved", "This one didn't go through". */
  settled?: StatusCard;
  /** Otherwise the server's word for a step waiting on someone else: "Reviewing application". */
  waiting?: string;
};

/**
 * The social deal header's status line, when there's no button to show. A
 * finished deal shows the server's card (its title as a pill, its words under
 * it); a deal waiting on Voltr or the brand shows that wait as a pill.
 */
export function SocialHeroStatus({ settled, waiting }: SocialHeroStatusProps) {
  if (settled) {
    return (
      <View style={styles.settled} testID="social-hero-settled">
        <Pill tone={settledTone(settled.tone)}>{settled.title}</Pill>
        {settled.body ? (
          <Text color="frost" testID="social-hero-settled-body">
            {settled.body}
          </Text>
        ) : null}
      </View>
    );
  }
  if (!waiting) return null;
  return (
    <Pill tone="outlineOnDark" style={styles.waiting} testID="social-hero-waiting">
      {waiting}
    </Pill>
  );
}

/** The server's tone → a pill that reads on Deep Green. Own keys only: a tone is server-sent. */
const TONE: Record<string, PillTone> = { success: 'green', danger: 'coral' };

function settledTone(tone: string): PillTone {
  return Object.prototype.hasOwnProperty.call(TONE, tone) ? (TONE[tone] as PillTone) : 'outlineOnDark';
}

const styles = StyleSheet.create({
  settled: { alignItems: 'flex-start', gap: spacing.sm },
  waiting: { alignSelf: 'flex-start' },
});
